import { api, absoluteUrl } from "@/api/client";
import { printHtml } from "@/lib/printDoc";

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[ch]);
const clip = (s, n) => (s && s.length > n ? s.slice(0, n).trimEnd() + "…" : s || "");

async function photoToDataUrl(photoId) {
  try {
    const res = await api.get(`/tracker/photos/${photoId}/file`, { responseType: "blob" });
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(res.data);
    });
  } catch { return ""; }
}

const bar = (pct, h = 6) => `<div style="height:${h}px;background:#f0f0f0;border-radius:99px;overflow:hidden;"><div style="height:100%;width:${Math.min(100, pct || 0)}%;background:#171717;"></div></div>`;

export async function printProjectSummary({ project, client, activities, photos, reports, workspace, towers = [], tower = null }) {
  const latest = (reports || []).find((r) => r.status === "finalized") || (reports || [])[0];
  const summary = latest?.content?.overall?.summary || "";
  const actName = Object.fromEntries((activities || []).map((a) => [a.id, a.name]));
  const pics = await Promise.all((photos || []).slice(0, 4).map(async (p) => ({ ...p, dataUrl: await photoToDataUrl(p.id) })));
  const applicable = (activities || []).filter((a) => a.is_applicable !== false);
  const today = new Date().toISOString().slice(0, 10);

  const actRows = applicable.map((a) => `
    <tr><td style="padding:4px 8px;font-size:11px;width:38%;">${esc(a.name)}</td>
    <td style="padding:4px 8px;width:42%;">${bar(a.completion_percent)}</td>
    <td style="padding:4px 8px;font-size:11px;text-align:right;" class="font-mono">${Math.round(a.completion_percent || 0)}%</td>
    <td style="padding:4px 8px;font-size:10px;color:#737373;">${esc(a.status)}</td></tr>`).join("");

  const photoBlock = pics.filter((p) => p.dataUrl).map((p) => `
    <div style="display:inline-block;width:23.5%;margin-right:1.5%;vertical-align:top;">
      <img src="${p.dataUrl}" style="width:100%;height:110px;object-fit:cover;border-radius:4px;" />
      <div style="font-size:9px;color:#737373;margin-top:2px;">${esc(clip(p.caption || actName[p.activity_id] || "", 40))}</div>
    </div>`).join("");

  const detail = (k, v) => `<div><span style="color:#737373;">${k}:</span> <b>${esc(v || "—")}</b></div>`;
  const html = `
    <div style="display:flex;justify-content:space-between;align-items:center;border-bottom:2px solid #171717;padding-bottom:12px;margin-bottom:16px;">
      <div style="display:flex;align-items:center;gap:10px;">
        ${workspace?.logo_url ? `<img src="${esc(absoluteUrl(workspace.logo_url))}" style="width:44px;height:44px;object-fit:contain;border-radius:4px;" />` : ""}
        <div>
          <div class="text-xl font-bold">${esc(workspace?.name || "Gray Owl Studio")}</div>
          ${workspace?.tagline ? `<div style="font-size:10px;color:#737373;text-transform:uppercase;letter-spacing:.05em;">${esc(workspace.tagline)}</div>` : ""}
        </div>
      </div>
      <div class="text-right"><div class="text-lg font-bold">Project Progress Summary</div><div class="text-xs text-neutral-500">As of ${today}</div></div>
    </div>
    <div style="display:flex;gap:24px;align-items:flex-start;margin-bottom:16px;">
      <div style="flex:1;font-size:11px;line-height:1.7;">
        <div class="text-lg font-bold" style="margin-bottom:4px;">${esc(project.name)}</div>
        ${detail("Client", client?.name)}${detail("Location", project.location)}${detail("Start date", project.start_date)}${detail("Status", project.status)}
      </div>
      <div style="width:38%;">
        <div style="font-size:10px;color:#737373;text-transform:uppercase;letter-spacing:.05em;">Overall completion</div>
        <div class="font-bold" style="font-size:36px;line-height:1.1;margin:4px 0 8px;">${project.overall_percent}%</div>
        ${bar(project.overall_percent, 10)}
      </div>
    </div>
    ${towers.length ? `<div class="section" style="margin-bottom:16px;">
      <div class="text-xs uppercase tracking-wider text-neutral-500" style="margin-bottom:6px;">Tower-wise Progress</div>
      <table style="width:100%;">${towers.map((t) => `<tr><td style="padding:4px 8px;font-size:11px;width:38%;"><b>${esc(t.name)}</b></td><td style="padding:4px 8px;width:50%;">${bar(t.percent)}</td><td style="padding:4px 8px;font-size:11px;text-align:right;">${t.percent}%</td></tr>`).join("")}</table>
    </div>` : ""}
    <div class="section" style="margin-bottom:16px;">
      <div class="text-xs uppercase tracking-wider text-neutral-500" style="margin-bottom:6px;">Activity Progress${tower ? ` — ${esc(tower.name)}` : ""}</div>
      <table style="width:100%;">${actRows || '<tr><td style="font-size:11px;">No activities yet.</td></tr>'}</table>
    </div>
    ${summary ? `<div class="section" style="margin-bottom:16px;">
      <div class="text-xs uppercase tracking-wider text-neutral-500" style="margin-bottom:6px;">Latest Site Report${latest?.visit_date ? " — " + esc(latest.visit_date) : ""}</div>
      <div style="font-size:11px;line-height:1.5;">${esc(clip(summary, 650))}</div>
    </div>` : ""}
    ${photoBlock ? `<div class="section" style="margin-bottom:12px;">
      <div class="text-xs uppercase tracking-wider text-neutral-500" style="margin-bottom:6px;">Latest Site Photos</div>${photoBlock}
    </div>` : ""}
    <div class="text-xs text-neutral-500" style="border-top:1px solid #d4d4d4;padding-top:6px;">Prepared by ${esc(workspace?.name || "Gray Owl Studio")} · ${today}</div>`;
  printHtml(html, { title: `Project Summary — ${project.name} — ${today}`, pageSize: "A4" });
}
